/** Create / edit dialog for a launch policy (react-hook-form + zod).
 *
 *  A launch policy is a named bundle of 12 feature flags. `tenant` is clearable:
 *  cleared = a system/global policy (`tour_operator: null`). Access tags are
 *  tenant-scoped, so the atag picker only appears once a tenant is chosen (a
 *  system/global policy has no owning operator to scope tags to — same rule as
 *  regions / trip types). Launch_Policy has no optimistic-concurrency token, so
 *  writes are last-writer-wins. */
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  create as createPolicy,
  update as updatePolicy,
  LAUNCH_POLICY_FLAGS,
  type LaunchPolicyDetail,
  type LaunchPolicyFlagKey,
  type LaunchPolicyWrite,
} from '@/api/launchPolicies';
import { rbacApi, type AccessTag } from '@/api/rbac';
import { AppDialog } from '@/components/AppDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';
import { singlePageTenant } from '@/scope/pageTenants';

import { launchPolicyActions, launchPolicyOperatorActions } from './launchPolicyLinks';

const FLAG_KEYS = LAUNCH_POLICY_FLAGS.map((f) => f.key);
/** Flags grouped for the form, in the order they should render. */
const FLAG_GROUPS = ['Messaging', 'Webhooks', 'Channels'] as const;

/** zod shape for the 12 flags — each a required boolean (defaulted in EMPTY). */
const flagShape = Object.fromEntries(FLAG_KEYS.map((k) => [k, z.boolean()])) as Record<
  LaunchPolicyFlagKey,
  z.ZodBoolean
>;

const FormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(256),
  description: z.string(),
  ...flagShape,
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY_FLAGS = Object.fromEntries(FLAG_KEYS.map((k) => [k, false])) as Record<
  LaunchPolicyFlagKey,
  boolean
>;

const EMPTY: FormValues = { name: '', description: '', ...EMPTY_FLAGS };

function toDefaults(policy: LaunchPolicyDetail | null): FormValues {
  if (!policy) return EMPTY;
  const flags = Object.fromEntries(FLAG_KEYS.map((k) => [k, Boolean(policy[k])])) as Record<
    LaunchPolicyFlagKey,
    boolean
  >;
  return { name: policy.name, description: policy.description ?? '', ...flags };
}

/** The detail atag arrives as NamedRef (name may be null); the picker deals in
 *  AccessTag (name: string). Coerce on seed. */
const seedAtags = (xs: { id: number; name: string | null }[]): AccessTag[] =>
  xs.map((a) => ({ id: a.id, name: a.name ?? '' }));

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  policy: LaunchPolicyDetail | null;
  /** The page's operators (D9), for create: the one is pre-selected, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function LaunchPolicyFormDialog({ open, policy, pageTenants, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = policy !== null;
  const showHistory = isEdit && hasPerm('console_audit_view');
  const [tab, setTab] = useState(0);

  // Tenant + atags live outside RHF (self-contained controlled inputs, like the
  // region editor).
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [atags, setAtags] = useState<AccessTag[]>([]);
  // Free-text "why" — travels as X-Change-Reason, lands on the change-history row.
  const [reason, setReason] = useState('');

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(policy),
  });

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setReason('');
    reset(toDefaults(policy));
    if (policy) {
      setTenant(
        policy.tenant
          ? { id: policy.tenant.id, name: policy.tenant.name, code: policy.tenant.code }
          : null,
      );
      setAtags(seedAtags(policy.atag));
    } else {
      setTenant(singlePageTenant(pageTenants));
      setAtags([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policy?.id]);

  // Clearing the tenant makes the policy system/global — access tags no longer
  // apply, so drop any that were selected.
  const tenantId = tenant?.id ?? null;
  useEffect(() => {
    if (tenantId === null && atags.length) setAtags([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const mutation = useMutation({
    mutationFn: (body: LaunchPolicyWrite) =>
      isEdit ? updatePolicy(policy.id, body, reason) : createPolicy(body, reason),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Launch policy updated.' : 'Launch policy created.', 'success');
      qc.invalidateQueries({ queryKey: ['launch_policies'] });
      onClose();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    const flags = Object.fromEntries(FLAG_KEYS.map((k) => [k, v[k]])) as Record<
      LaunchPolicyFlagKey,
      boolean
    >;
    mutation.mutate({
      tour_operator: tenantId,
      name: v.name.trim(),
      description: v.description.trim() || null,
      atag: tenantId === null ? [] : atags.map((a) => a.id),
      ...flags,
    });
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title={
        isEdit ? (
          <>
            Edit launch policy ·{' '}
            <ValueActions actions={launchPolicyActions(policy, policy.tenant?.id ?? null)}>
              {policy.name}
            </ValueActions>
          </>
        ) : (
          'New launch policy'
        )
      }
      disableBackdropClose
      actions={
        tab === 0 ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create policy'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {showHistory && (
        <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 1 }}>
          <Tab label="Editor" />
          <Tab label="Change history" />
        </Tabs>
      )}
      {tab === 1 && policy !== null ? (
        <HistoryPanel
          contentType="demo.launch_policy"
          objectPk={String(policy.id)}
          onRestored={() => void qc.invalidateQueries({ queryKey: ['launch_policies'] })}
        />
      ) : (
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <TextField
          {...register('name')}
          label="Name"
          size="small"
          fullWidth
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
        />
        <TextField
          {...register('description')}
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          error={Boolean(errors.description)}
          helperText={errors.description?.message ?? 'Optional.'}
        />

        <Box>
          <TenantPicker
            value={tenant}
            featured={isEdit ? undefined : pageTenants}
            onChange={setTenant}
            label="Operator"
            placeholder="System / global (no operator)"
          />
          {tenant && (
            // The picker owns its click; the links hang off the picked value.
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              <ValueActions actions={launchPolicyOperatorActions(tenant)}>
                {tenant.code ?? tenant.name}
              </ValueActions>
            </Typography>
          )}
        </Box>

        <Divider />

        {FLAG_GROUPS.map((group) => (
          <Box key={group}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              {group}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {LAUNCH_POLICY_FLAGS.filter((f) => f.group === group).map((f) => (
                <Controller
                  key={f.key}
                  control={control}
                  name={f.key}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={f.label}
                    />
                  )}
                />
              ))}
            </Box>
          </Box>
        ))}

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Access
          </Typography>
          {tenantId != null ? (
            <RemoteAutocomplete<AccessTag>
              label="Access tags"
              size="small"
              multiple
              fetchOnEmpty
              value={atags}
              getOptionLabel={(o) => o.name}
              getOptionKey={(o) => o.id}
              queryFn={async (q) => (await rbacApi.listAccessTags(tenantId, q)).results}
              onChange={(v) => setAtags((v as AccessTag[]) ?? [])}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Access tags are unavailable — a system / global policy has no owning operator.
            </Typography>
          )}
        </Box>

        <TextField
          label="Reason for change"
          size="small"
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText="Optional — stored in the change history."
        />
      </Box>
      )}
    </AppDialog>
  );
}
